import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Camera, X } from "lucide-react";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Card, CardContent } from "../ui/card";
import { Label } from "../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { cn } from "../../lib/utils";
import { CameraCaptureDialog } from "./CameraCaptureDialog";

const STATUS_STYLES = {
  inside: "bg-green-100 text-green-800 border-green-200",
  completed: "bg-slate-100 text-slate-700 border-slate-200",
  denied: "bg-red-100 text-red-800 border-red-200",
  pending_approval: "bg-amber-100 text-amber-800 border-amber-200",
  approved: "bg-blue-100 text-blue-800 border-blue-200",
  rejected: "bg-red-100 text-red-800 border-red-200",
  revoked: "bg-slate-200 text-slate-700 border-slate-300",
  cancelled: "bg-slate-100 text-slate-500 border-slate-200",
  expired: "bg-slate-100 text-slate-500 border-slate-200",
};

const SEVERITY_STYLES = {
  low: "bg-slate-100 text-slate-700 border-slate-200",
  medium: "bg-amber-100 text-amber-800 border-amber-200",
  high: "bg-orange-100 text-orange-800 border-orange-200",
  critical: "bg-red-100 text-red-800 border-red-200",
};

const INCIDENT_STATUS_STYLES = {
  open: "bg-red-100 text-red-800 border-red-200",
  in_progress: "bg-amber-100 text-amber-800 border-amber-200",
  resolved: "bg-green-100 text-green-800 border-green-200",
  closed: "bg-slate-100 text-slate-600 border-slate-200",
};

// Preautorización aprobada pero vencida o sin usos disponibles
export const effectivePreauthStatus = (p) => {
  if (p.status === "approved") {
    if (new Date(p.valid_until) < new Date() || p.uses >= p.max_uses) return "expired";
  }
  return p.status;
};

export const StatusBadge = ({ status }) => {
  const { t } = useTranslation();
  return (
    <Badge variant="outline" className={cn("whitespace-nowrap", STATUS_STYLES[status])}>
      {t(`visitors.status.${status}`)}
    </Badge>
  );
};

export const SeverityBadge = ({ severity }) => {
  const { t } = useTranslation();
  return (
    <Badge variant="outline" className={cn("whitespace-nowrap", SEVERITY_STYLES[severity])}>
      {t(`visitors.severity.${severity}`)}
    </Badge>
  );
};

export const IncidentStatusBadge = ({ status }) => {
  const { t } = useTranslation();
  return (
    <Badge variant="outline" className={cn("whitespace-nowrap", INCIDENT_STATUS_STYLES[status])}>
      {t(`visitors.incidentStatus.${status}`)}
    </Badge>
  );
};

export const PageHeader = ({ title, subtitle, actions }) => (
  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
    <div>
      <h1 className="font-heading text-2xl sm:text-3xl font-bold text-slate-900">{title}</h1>
      {subtitle && <p className="text-slate-600 mt-1">{subtitle}</p>}
    </div>
    {actions && <div className="flex flex-wrap gap-2">{actions}</div>}
  </div>
);

export const StatCard = ({ icon: Icon, label, value, tone = "slate", onClick }) => {
  const tones = {
    slate: "bg-slate-100 text-slate-700",
    green: "bg-green-100 text-green-700",
    blue: "bg-blue-100 text-blue-700",
    amber: "bg-amber-100 text-amber-700",
    red: "bg-red-100 text-red-700",
    violet: "bg-violet-100 text-violet-700",
  };
  return (
    <Card className={cn(onClick && "cursor-pointer hover:shadow-md transition-shadow")} onClick={onClick}>
      <CardContent className="p-4 flex items-center gap-3">
        <div className={cn("w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0", tones[tone])}>
          <Icon className="h-5 w-5" />
        </div>
        <div className="min-w-0">
          <p className="text-2xl font-bold text-slate-900 leading-tight">{value ?? "—"}</p>
          <p className="text-xs text-slate-500 leading-tight line-clamp-2">{label}</p>
        </div>
      </CardContent>
    </Card>
  );
};

/** Selectores de compañía (admin/revendedor) e instalación. */
export const ScopeFilters = ({ scope, showFacility = true, allowAllFacilities = true, children }) => {
  const { t } = useTranslation();
  return (
    <Card>
      <CardContent className="p-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 items-end">
          {scope.canSelectCompany && (
            <div>
              <Label className="text-sm text-slate-600 mb-1 block">{t("visitors.common.company")}</Label>
              <Select value={scope.companyId} onValueChange={scope.setCompanyId}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t("visitors.common.allCompanies")}</SelectItem>
                  {scope.companies.map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          {showFacility && (
            <div>
              <Label className="text-sm text-slate-600 mb-1 block">{t("visitors.common.facility")}</Label>
              <Select value={scope.facilityId} onValueChange={scope.setFacilityId}>
                <SelectTrigger>
                  <SelectValue placeholder={t("visitors.common.selectFacility")} />
                </SelectTrigger>
                <SelectContent>
                  {allowAllFacilities && <SelectItem value="all">{t("visitors.common.allFacilities")}</SelectItem>}
                  {scope.facilities.map((f) => (
                    <SelectItem key={f.id} value={f.id}>
                      {f.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          {children}
        </div>
      </CardContent>
    </Card>
  );
};

/** Foto tomada en el momento con la cámara del dispositivo (no se cargan archivos). */
export const PhotoInput = ({ value, onChange, label }) => {
  const { t } = useTranslation();
  const [cameraOpen, setCameraOpen] = useState(false);

  return (
    <div>
      {label && <Label className="mb-1 block">{label}</Label>}
      <CameraCaptureDialog open={cameraOpen} onOpenChange={setCameraOpen} onCapture={onChange} title={label} />
      {value ? (
        <div className="relative inline-block">
          <img src={value} alt="" className="h-28 w-28 object-cover rounded-lg border" />
          <button
            type="button"
            onClick={() => onChange(null)}
            className="absolute -top-2 -right-2 bg-white border rounded-full p-1 shadow"
            aria-label={t("visitors.common.removePhoto")}
          >
            <X className="h-3 w-3" />
          </button>
        </div>
      ) : (
        <Button type="button" variant="outline" size="sm" onClick={() => setCameraOpen(true)}>
          <Camera className="h-4 w-4 mr-2" />
          {t("visitors.common.takePhoto")}
        </Button>
      )}
    </div>
  );
};

/** Indica que no hay instalaciones configuradas y a dónde ir para crearlas. */
export const NoFacilitiesNotice = ({ isManager }) => {
  const { t } = useTranslation();
  return (
    <Card className="border-amber-200 bg-amber-50">
      <CardContent className="p-4 text-sm text-amber-900">
        {t("visitors.setup.noFacilities")} {isManager && t("visitors.setup.noFacilitiesHint")}
      </CardContent>
    </Card>
  );
};
